<script setup lang="ts">
import { useProductModal } from '@/composables/useProductModal'

import { useCityStore } from '@/store/city/city'
import { useUserStore } from '@/store/user/user'

import { Sliders, SliderType } from '@/types/api/api-v4/slider'

import AppSeoDescription from '@/components/App/AppSeoDescription.vue'
import MainAppFactoid from '@/components/Main/MainAppFactoid.vue'
import MainCategoryBannerMobile from '@/components/Main/MainCategoryBannerMobile.vue'
import MainCategoryProductList from '@/components/Main/MainCategoryProductList.vue'
import MainEmailFactoid from '@/components/Main/MainEmailFactoid.vue'
import ProductModal from '@/components/Product/ProductModal.vue'
const MainCategoryList = defineAsyncComponent(
  () => import('@/components/Main/MainCategoryList.vue'),
)

const cityStore = useCityStore()
const userStore = useUserStore()

type Props = {
  sliders?: Sliders | null
  seoContent?: string
}

withDefaults(defineProps<Props>(), {
  sliders: null,
  seoContent: '',
})

const { isDesktop } = useDevice()

const checkCountry = computed(() => {
  return cityStore.cityInfo?.country?.name === 'Россия'
})

const isAuthenticated = computed(() => {
  return userStore.isAuthenticated
})

const { productDialog, openDialogProduct, closeDialogProduct } =
  useProductModal()
</script>

<template>
  <div class="main__page">
    <main-category-banner-mobile v-if="!isDesktop" />

    <main-category-list v-if="!isDesktop" />

    <div v-if="isDesktop" class="main__page-desktop">
      <div class="product__list">
        <!-- Слайдер акций -->
        <div v-if="sliders?.saleSlider" class="product__list-sale">
          <main-category-product-list
            v-for="slide in sliders.saleSlider"
            :key="slide.name"
            :slide="slide"
            :slider-type="SliderType.SALE"
            @open-dialog-product="openDialogProduct"
          />
        </div>

        <!-- Обычный слайдер -->
        <div v-if="sliders?.sliders" class="product__list-normal">
          <main-category-product-list
            v-for="slide in sliders.sliders"
            :key="slide.name"
            :slide="slide"
            :slider-type="SliderType.SIMPLE"
            @open-dialog-product="openDialogProduct"
          />
        </div>

        <!-- Слайдер популярных товаров-->
        <div v-if="sliders?.popularSlider" class="product__list-popular">
          <main-category-product-list
            v-for="slide in sliders.popularSlider"
            :key="slide.name"
            :slide="slide"
            :slider-type="SliderType.POPULAR"
            @open-dialog-product="openDialogProduct"
          />
        </div>
      </div>
    </div>

    <app-seo-description
      v-if="seoContent"
      :seo-content="seoContent"
      class="main__page-seo"
    />

    <client-only>
      <main-app-factoid v-if="checkCountry" class="main__page-app" />

      <main-email-factoid v-if="isAuthenticated" class="main__page-email" />
    </client-only>

    <product-modal
      :product-dialog="productDialog"
      @close-dialog-product="closeDialogProduct"
    />
  </div>
</template>

<style lang="scss" scoped>
.main__page {
  min-height: 100vh;

  &-seo {
    margin-bottom: 100px;

    @include media('<desktop-mini') {
      margin-bottom: 120px;
    }
  }

  &-app {
    margin-bottom: 100px;

    @include media('<desktop-mini') {
      margin-bottom: 40px;
    }
  }

  &-email {
    margin-bottom: 50px;
  }

  &-desktop {
    .product__skeletons {
      display: grid;
      grid-template-columns: repeat(4, 1fr);
      gap: 100px 36px;
      justify-content: space-between;
      padding-bottom: 90px;
    }
  }
}
</style>
